import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, Settings, ChevronDown } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';

// New simplified, institutional navigation aligned with URBANIK 2026 strategic positioning.
// Sub-items targeting non-existent pages use hash anchors on existing routes (zero new routes, zero breakage).
const navLinks = [
  { name: 'Accueil', path: '/' },
  { name: 'Le Forum', path: '/evenement', children: [
    { name: 'Présentation', path: '/evenement#presentation' },
    { name: 'Mot du Président', path: '/evenement#president' },
    { name: 'Objectifs du forum', path: '/evenement#objectifs' },
  ]},
  { name: 'Programme', path: '/programme', children: [
    { name: 'Conférences & panels', path: '/programme#conferences' },
    { name: 'Ateliers', path: '/programme#ateliers' },
    { name: 'Networking B2B', path: '/programme#networking' },
  ]},
  { name: 'Exposition', path: '/exposition', children: [
    { name: 'Devenir exposant', path: '/exposition/devenir-exposant' },
    { name: 'Stands & opportunités', path: '/exposition/stands' },
  ]},
  { name: 'Partenaires', path: '/partenaires', children: [
    { name: 'Partenaires officiels', path: '/partenaires' },
    { name: 'Devenir partenaire', path: '/participer#partenaire' },
  ]},
  { name: 'Galerie', path: '/medias' },
  { name: 'Actualités', path: '/actualites' },
  { name: 'Participer', path: '/participer', highlight: true, children: [
    { name: 'S\'inscrire', path: '/participer#inscription' },
    { name: 'Télécharger la brochure', path: '/participer#brochure' },
    { name: 'Contact', path: '/participer#contact' },
  ]},
];

const Navbar = ({ onOpenAuthModal }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [dropdown, setDropdown] = useState(null);
  const dropRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout, isAdmin } = useAuth();
  const isHome = location.pathname === '/';

  useEffect(() => {
    const h = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', h);
    return () => window.removeEventListener('scroll', h);
  }, []);

  useEffect(() => {
    const h = (e) => { if (dropRef.current && !dropRef.current.contains(e.target)) setDropdown(null); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  useEffect(() => { setMobileOpen(false); setDropdown(null); }, [location.pathname]);

  const isActive = (path) => path === '/' ? location.pathname === '/' : location.pathname.startsWith(path);
  const solid = !isHome || scrolled;

  // Resolve nav link className (desktop) — avoids nested ternaries inline.
  const desktopLinkClass = (link) => {
    const base = 'px-3 py-2 text-[13px] font-medium rounded-md transition-colors';
    if (link.highlight) return `${base} bg-[#F5A623] text-[#1E3A5F] hover:bg-[#E09615]`;
    if (isActive(link.path)) return `${base} text-[#F5A623]`;
    if (solid) return `${base} text-[#4A5568] hover:text-[#1E3A5F] hover:bg-[#F7F9FC]`;
    return `${base} text-white/80 hover:text-white`;
  };

  const dropdownTriggerClass = (link) => `${desktopLinkClass(link)} flex items-center gap-1`;

  const dropdownChildClass = (child) => {
    const base = 'block px-4 py-2.5 text-sm';
    if (isActive(child.path.split('#')[0])) return `${base} text-[#1E3A5F] bg-[#F7F9FC] font-medium`;
    return `${base} text-[#4A5568] hover:bg-[#F7F9FC]`;
  };

  const mobileLinkClass = (link) => {
    const base = 'block py-2.5 px-3 text-sm rounded-md';
    if (link.highlight) return `${base} bg-[#F5A623] text-[#1E3A5F] font-semibold mt-2`;
    if (isActive(link.path)) return `${base} text-[#1E3A5F] font-medium bg-[#F7F9FC]`;
    return `${base} text-[#4A5568]`;
  };

  const mobileChildClass = (child) => {
    const base = 'block py-2.5 px-5 text-sm rounded-md';
    if (isActive(child.path.split('#')[0])) return `${base} text-[#1E3A5F] font-medium bg-[#F7F9FC]`;
    return `${base} text-[#4A5568]`;
  };

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${solid ? 'bg-white shadow-sm border-b border-[#E1E8F0]' : 'bg-black/10 backdrop-blur-sm'}`} data-testid="navbar">
      <div className="container-institutional">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center" data-testid="navbar-logo">
            <img
              src="/logo-urbanik.png"
              alt="URBANIK"
              className={`h-10 w-auto transition-all duration-300 ${solid ? '' : 'brightness-0 invert'}`}
            />
          </Link>

          {/* Desktop */}
          <div className="hidden xl:flex items-center gap-0.5" ref={dropRef}>
            {navLinks.map(link => link.children ? (
              <div key={link.path + link.name} className="relative">
                <button onClick={() => setDropdown(dropdown === link.name ? null : link.name)}
                  className={`px-3 py-2 text-[13px] font-medium rounded-md transition-colors flex items-center gap-1 ${link.highlight ? 'bg-[#F5A623] text-[#1E3A5F] hover:bg-[#E09615]' : isActive(link.path) ? 'text-[#F5A623]' : solid ? 'text-[#4A5568] hover:text-[#1E3A5F] hover:bg-[#F7F9FC]' : 'text-white/80 hover:text-white'}`}
                  data-testid={`nav-${link.name.toLowerCase().replace(/[\s']/g, '-')}`}>
                  {link.name}<ChevronDown className={`w-3 h-3 transition-transform ${dropdown === link.name ? 'rotate-180' : ''}`} />
                </button>
                {dropdown === link.name && (
                  <div className="absolute top-full left-0 mt-1 w-56 bg-white border border-[#E1E8F0] rounded-lg shadow-lg py-1 z-50">
                    {link.children.map(child => (
                      <Link key={child.path + child.name} to={child.path} onClick={() => setDropdown(null)}
                        className={`block px-4 py-2.5 text-sm ${isActive(child.path.split('#')[0]) ? 'text-[#1E3A5F] bg-[#F7F9FC] font-medium' : 'text-[#4A5568] hover:bg-[#F7F9FC]'}`}
                        data-testid={`nav-sub-${child.name.toLowerCase().replace(/[\s']/g, '-')}`}>{child.name}</Link>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <Link key={link.path} to={link.path}
                className={`px-3 py-2 text-[13px] font-medium rounded-md transition-colors ${link.highlight ? 'bg-[#F5A623] text-[#1E3A5F] hover:bg-[#E09615]' : isActive(link.path) ? 'text-[#F5A623]' : solid ? 'text-[#4A5568] hover:text-[#1E3A5F] hover:bg-[#F7F9FC]' : 'text-white/80 hover:text-white'}`}
                data-testid={`nav-${link.name.toLowerCase()}`}>{link.name}</Link>
            ))}
            {user && isAdmin && (
              <button onClick={() => navigate('/admin')} className={`ml-1 p-2 rounded-md ${solid ? 'text-[#94A3B8] hover:text-[#1E3A5F] hover:bg-[#F7F9FC]' : 'text-white/40 hover:text-white/70'}`} data-testid="admin-menu-trigger"><Settings className="w-4 h-4" /></button>
            )}
          </div>

          {/* Mobile */}
          <button className={`xl:hidden p-2 ${solid ? 'text-[#1E3A5F]' : 'text-white'}`} onClick={() => setMobileOpen(!mobileOpen)} data-testid="mobile-menu-toggle">
            {mobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="xl:hidden bg-white border-t border-[#E1E8F0] shadow-lg max-h-[80vh] overflow-y-auto" data-testid="mobile-menu">
          <div className="container-institutional py-3 space-y-0.5">
            {navLinks.map(link => link.children ? (
              <div key={link.path + link.name}>
                <p className="px-3 pt-3 pb-1 text-xs font-semibold uppercase tracking-wider text-[#94A3B8]">{link.name}</p>
                {link.children.map(child => (
                  <Link key={child.path + child.name} to={child.path} className={mobileChildClass(child)}>{child.name}</Link>
                ))}
              </div>
            ) : (
              <Link key={link.path} to={link.path}
                className={mobileLinkClass(link)}>{link.name}</Link>
            ))}
            {user && isAdmin && (
              <div className="pt-2 mt-2 border-t border-[#E1E8F0]">
                <button className="w-full text-left py-2.5 px-3 text-sm text-[#4A5568] rounded-md" onClick={() => navigate('/admin')}>Administration</button>
                <button className="w-full text-left py-2.5 px-3 text-sm text-red-600 rounded-md" onClick={() => { logout(); navigate('/'); }}>Déconnexion</button>
              </div>
            )}
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
